import type { PvcConfiguration, PvcQuoteDomainRejection } from "./pricing-controller";
import {
  addIsraeliProductionDays,
  isIsraeliProductionDay,
  PVC_STANDARD_PRODUCTION_DAYS,
  todayInIsrael,
} from "./production-calendar";

/* ============================================================
   Types
   ============================================================ */

export type ConfiguratorField =
  | "widthCm" | "heightCm" | "quantity" | "materialId" | "addonIds" | "requestedDate";

/** DOM order. The focus rule depends on this being deterministic. */
export const FIELD_DOM_ORDER: ConfiguratorField[] = [
  "widthCm", "heightCm", "quantity", "materialId", "addonIds", "requestedDate",
];

export type FieldError = { code: string; message?: string };

/**
 * The subset of a server rejection that can affect configurator fields.
 * Quote responses may contain correlation metadata as well, but the
 * configurator deliberately does not know or care about it.
 */
export type ServerFieldRejection = Pick<
  PvcQuoteDomainRejection,
  "code" | "fieldErrors" | "invalidOptionIds" | "earliestAvailableDate" | "currentSchemaVersion"
>;

export type ValidationState = {
  clientErrors: Partial<Record<ConfiguratorField, FieldError>>;
  serverErrors: Partial<Record<ConfiguratorField, FieldError>>;
};

/** Raw precedence: a server error always wins over a client error. */
export function getVisibleError(v: ValidationState, field: ConfiguratorField): FieldError | null {
  return v.serverErrors[field] ?? v.clientErrors[field] ?? null;
}

/**
 * An error can EXIST without being shown. Free-text fields stay silent until
 * the user leaves them or tries to submit; discrete choices surface at once.
 */
export function shouldShowFieldError(state: ConfiguratorState, field: ConfiguratorField): boolean {
  if (state.submitAttempted) return true;
  switch (field) {
    case "widthCm": return state.width.touched;
    case "heightCm": return state.height.touched;
    case "quantity": return state.quantity.touched;
    default: return true;
  }
}

/** What the UI renders. Server errors are always visible. */
export function getDisplayedError(state: ConfiguratorState, field: ConfiguratorField): FieldError | null {
  const server = state.validation.serverErrors[field];
  if (server) return server;
  if (!shouldShowFieldError(state, field)) return null;
  return state.validation.clientErrors[field] ?? null;
}

export type DimensionFieldState = {
  raw: string;
  value: number | null;
  touched: boolean;
  /** Bumped on every keystroke. A debounced commit carrying a stale one is dropped. */
  editRevision: number;
};

export type QuantityFieldState = {
  raw: string;
  value: number | null;
  touched: boolean;
  editRevision: number;
};

export type ProductConstraints = {
  minWidthCm: number; maxWidthCm: number;
  minHeightCm: number; maxHeightCm: number;
  stepCm: number;
  minQuantity: number; maxSelfServeQuantity: number;
};

export type MaterialOption = { id: string; available: boolean };
export type AddonOption = { id: string; available: boolean };

export type ConfiguratorSchema = {
  schemaVersion: number;
  constraints: ProductConstraints;
  materials: MaterialOption[];
  addons: AddonOption[];
};

export type ConfiguratorState = {
  width: DimensionFieldState;
  height: DimensionFieldState;
  quantity: QuantityFieldState;
  materialId: string | null;
  addonIds: string[];
  requestedDate: string | null;
  validation: ValidationState;
  submitAttempted: boolean;
};

export type ConfiguratorEffect =
  | { type: "PRICING_DIRTY" }
  | { type: "PRICING_READY"; configuration: PvcConfiguration }
  | { type: "PRICING_INVALID" }
  | { type: "NONE" };

export type ConfiguratorReconciliationResult = {
  removedAddonIds: string[];
  materialInvalidated: boolean;
  effect: ConfiguratorEffect;
};

export type ConfiguratorDraftSnapshot = {
  widthRaw: string;
  heightRaw: string;
  quantityRaw: string;
  materialId: string | null;
  addonIds: string[];
  requestedDate: string | null;
};

export type ConfiguratorSubmitResult =
  | { valid: true; configuration: PvcConfiguration }
  | { valid: false; errorFields: ConfiguratorField[] };

export type DimensionField = "widthCm" | "heightCm";

export interface ConfiguratorController {
  getState(): ConfiguratorState;
  getPricingConfiguration(): PvcConfiguration | null;
  setDimensionInput(field: DimensionField, raw: string): ConfiguratorEffect;
  /** Pass the editRevision the debounce was scheduled on; a stale one is dropped. */
  commitDimension(field: DimensionField, editRevision?: number): ConfiguratorEffect;
  getDimensionEditRevision(field: DimensionField): number;
  /** Discrete set (used by the stepper and by hydration). */
  setQuantity(quantity: number): ConfiguratorEffect;
  stepQuantity(delta: -1 | 1): ConfiguratorEffect;
  setQuantityInput(raw: string): ConfiguratorEffect;
  commitQuantity(editRevision?: number): ConfiguratorEffect;
  getQuantityEditRevision(): number;
  setMaterial(materialId: string): ConfiguratorEffect;
  toggleAddon(addonId: string, selected: boolean): ConfiguratorEffect;
  setRequestedDate(date: string | null): ConfiguratorEffect;
  applyServerRejection(rejection: ServerFieldRejection): ConfiguratorEffect;
  replaceSchema(schema: ConfiguratorSchema): ConfiguratorReconciliationResult;
  getDraftSnapshot(): ConfiguratorDraftSnapshot;
  restoreDraft(snapshot: ConfiguratorDraftSnapshot): ConfiguratorReconciliationResult;
  validateForSubmit(): ConfiguratorSubmitResult;
  subscribe(listener: (state: ConfiguratorState) => void): () => void;
  dispose(): void;
}

/* ============================================================
   Pure validators
   ============================================================ */

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function parseDimensionInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

export function validateDimension(
  value: number | null,
  { min, max, step }: { min: number; max: number; step: number }
): FieldError | null {
  if (value === null || !Number.isFinite(value)) return { code: "REQUIRED" };
  if (value < min) return { code: "BELOW_MIN" };
  if (value > max) return { code: "ABOVE_MAX" };
  const steps = (value - min) / step;
  if (Math.abs(steps - Math.round(steps)) >= 1e-9) return { code: "INVALID_STEP" };
  return null;
}

export function validateQuantity(
  quantity: number,
  { minQuantity, maxSelfServeQuantity }: ProductConstraints
): FieldError | null {
  if (!Number.isFinite(quantity) || !Number.isInteger(quantity)) return { code: "QUANTITY_INVALID" };
  if (quantity < minQuantity) return { code: "QUANTITY_TOO_LOW" };
  if (quantity > maxSelfServeQuantity) return { code: "QUANTITY_EXCEEDS_SELF_SERVE" };
  return null;
}

export function isValidDateShape(date: string | null): boolean {
  if (date === null) return true;
  if (!DATE_RE.test(date)) return false;
  const ms = Date.parse(`${date}T00:00:00Z`);
  return Number.isFinite(ms);
}

function canonicalAddonIds(ids: string[]): string[] {
  return [...new Set(ids)].sort();
}

/* ============================================================
   Controller
   ============================================================ */

export function createConfiguratorController(options: {
  schema: ConfiguratorSchema;
  initial?: {
    widthCm?: number | null;
    heightCm?: number | null;
    quantity?: number;
    materialId?: string | null;
    addonIds?: string[];
    requestedDate?: string | null;
  };
}): ConfiguratorController {
  let schema = options.schema;
  let disposed = false;
  const listeners = new Set<(state: ConfiguratorState) => void>();

  function makeQuantity(value: number | null): QuantityFieldState {
    return { raw: value === null ? "" : String(value), value, touched: false, editRevision: 0 };
  }

  function makeDimension(value: number | null): DimensionFieldState {
    return { raw: value === null ? "" : String(value), value, touched: false, editRevision: 0 };
  }

  let state: ConfiguratorState = {
    width: makeDimension(options.initial?.widthCm ?? null),
    height: makeDimension(options.initial?.heightCm ?? null),
    quantity: makeQuantity(options.initial?.quantity ?? schema.constraints.minQuantity),
    materialId: options.initial?.materialId ?? null,
    addonIds: canonicalAddonIds(options.initial?.addonIds ?? []),
    requestedDate: options.initial?.requestedDate ?? null,
    validation: { clientErrors: {}, serverErrors: {} },
    submitAttempted: false,
  };

  function emit() {
    if (disposed) return;
    for (const l of listeners) l(state);
  }

  function setState(next: Partial<ConfiguratorState>) {
    state = { ...state, ...next };
    emit();
  }

  function setClientError(field: ConfiguratorField, error: FieldError | null) {
    const clientErrors = { ...state.validation.clientErrors };
    if (error) clientErrors[field] = error; else delete clientErrors[field];
    state = { ...state, validation: { ...state.validation, clientErrors } };
  }

  function clearServerError(field: ConfiguratorField) {
    if (!(field in state.validation.serverErrors)) return;
    const serverErrors = { ...state.validation.serverErrors };
    delete serverErrors[field];
    state = { ...state, validation: { ...state.validation, serverErrors } };
  }

  function dimensionBounds(field: DimensionField) {
    const c = schema.constraints;
    return field === "widthCm"
      ? { min: c.minWidthCm, max: c.maxWidthCm, step: c.stepCm }
      : { min: c.minHeightCm, max: c.maxHeightCm, step: c.stepCm };
  }

  function materialIsSelectable(id: string | null): boolean {
    if (id === null) return false;
    return schema.materials.some(m => m.id === id && m.available);
  }

  function addonIsSelectable(id: string): boolean {
    return schema.addons.some(a => a.id === id && a.available);
  }

  function requestedDateError(date: string | null): FieldError | null {
    if (!isValidDateShape(date)) return { code: "DATE_INVALID_FORMAT" };
    if (date === null) return null;
    if (date < addIsraeliProductionDays(todayInIsrael(), 1) || !isIsraeliProductionDay(date)) {
      return { code: "DATE_UNAVAILABLE" };
    }
    return null;
  }

  /** Full validity check. Does not mutate. */
  function computeConfiguration(): PvcConfiguration | null {
    const w = state.width.value;
    const h = state.height.value;
    if (validateDimension(w, dimensionBounds("widthCm"))) return null;
    if (validateDimension(h, dimensionBounds("heightCm"))) return null;
    if (state.quantity.value === null) return null;
    if (validateQuantity(state.quantity.value, schema.constraints)) return null;
    if (!materialIsSelectable(state.materialId)) return null;
    if (!state.addonIds.every(addonIsSelectable)) return null;
    if (requestedDateError(state.requestedDate)) return null;

    return {
      widthCm: w as number,
      heightCm: h as number,
      quantity: state.quantity.value,
      materialId: state.materialId as string,
      addonIds: canonicalAddonIds(state.addonIds),
      requestedDate: state.requestedDate,
    };
  }

  function effectFromCurrentConfiguration(): ConfiguratorEffect {
    const configuration = computeConfiguration();
    return configuration ? { type: "PRICING_READY", configuration } : { type: "PRICING_INVALID" };
  }

  /* ---------- dimensions ---------- */

  function setDimensionInput(field: DimensionField, raw: string): ConfiguratorEffect {
    const key = field === "widthCm" ? "width" : "height";
    const current = state[key];
    const value = parseDimensionInput(raw);

    // Mid-typing: never surface a range error, and drop any stale server error.
    clearServerError(field);
    setClientError(field, null);

    setState({
      [key]: { raw, value, touched: current.touched, editRevision: current.editRevision + 1 },
    } as Partial<ConfiguratorState>);

    return { type: "PRICING_DIRTY" };
  }

  function getDimensionEditRevision(field: DimensionField): number {
    return field === "widthCm" ? state.width.editRevision : state.height.editRevision;
  }

  function commitDimension(field: DimensionField, editRevision?: number): ConfiguratorEffect {
    const key = field === "widthCm" ? "width" : "height";
    const current = state[key];

    // Client-side race guard: a debounced commit for an older keystroke is dropped.
    if (editRevision !== undefined && editRevision !== current.editRevision) {
      return { type: "NONE" };
    }

    const error = validateDimension(current.value, dimensionBounds(field));
    setClientError(field, error);
    setState({ [key]: { ...current, touched: true } } as Partial<ConfiguratorState>);

    return effectFromCurrentConfiguration();
  }

  /* ---------- quantity ---------- */

  function setQuantity(quantity: number): ConfiguratorEffect {
    clearServerError("quantity");
    const error = validateQuantity(quantity, schema.constraints);
    setClientError("quantity", error);
    setState({
      quantity: {
        raw: String(quantity),
        value: quantity,
        touched: true,
        editRevision: state.quantity.editRevision + 1,
      },
    });
    return effectFromCurrentConfiguration();
  }

  function stepQuantity(delta: -1 | 1): ConfiguratorEffect {
    const current = state.quantity.value ?? schema.constraints.minQuantity;
    return setQuantity(current + delta);
  }

  function setQuantityInput(raw: string): ConfiguratorEffect {
    clearServerError("quantity");
    setClientError("quantity", null);
    setState({
      quantity: {
        raw,
        value: parseDimensionInput(raw),
        touched: state.quantity.touched,
        editRevision: state.quantity.editRevision + 1,
      },
    });
    return { type: "PRICING_DIRTY" };
  }

  function commitQuantity(editRevision?: number): ConfiguratorEffect {
    const current = state.quantity;
    if (editRevision !== undefined && editRevision !== current.editRevision) {
      return { type: "NONE" };
    }
    const error =
      current.value === null
        ? { code: "REQUIRED" }
        : validateQuantity(current.value, schema.constraints);
    setClientError("quantity", error);
    setState({ quantity: { ...current, touched: true } });
    return effectFromCurrentConfiguration();
  }

  function getQuantityEditRevision(): number {
    return state.quantity.editRevision;
  }

  /* ---------- material ---------- */

  function setMaterial(materialId: string): ConfiguratorEffect {
    clearServerError("materialId");
    if (!materialIsSelectable(materialId)) {
      // No silent fallback: the selection is refused and the error is surfaced.
      setClientError("materialId", { code: "MATERIAL_UNAVAILABLE" });
      emit();
      return { type: "PRICING_INVALID" };
    }
    setClientError("materialId", null);
    setState({ materialId });
    return effectFromCurrentConfiguration();
  }

  /* ---------- addons ---------- */

  function toggleAddon(addonId: string, selected: boolean): ConfiguratorEffect {
    clearServerError("addonIds");

    if (selected && !addonIsSelectable(addonId)) {
      setClientError("addonIds", { code: "ADDON_UNAVAILABLE" });
      emit();
      return { type: "PRICING_INVALID" };
    }

    const next = selected
      ? canonicalAddonIds([...state.addonIds, addonId])
      : canonicalAddonIds(state.addonIds.filter(id => id !== addonId));

    setClientError("addonIds", null);
    setState({ addonIds: next });
    return effectFromCurrentConfiguration();
  }

  /* ---------- date ---------- */

  function setRequestedDate(date: string | null): ConfiguratorEffect {
    clearServerError("requestedDate");
    const today = todayInIsrael();
    const error = requestedDateError(date);
    const rushRequired = !error && date !== null &&
      date < addIsraeliProductionDays(today, PVC_STANDARD_PRODUCTION_DAYS);
    const addonIds = rushRequired && addonIsSelectable("rush")
      ? canonicalAddonIds([...state.addonIds, "rush"])
      : canonicalAddonIds(state.addonIds.filter(id => id !== "rush"));
    setClientError("requestedDate", error);
    setState({ requestedDate: date, addonIds });
    return effectFromCurrentConfiguration();
  }

  /* ---------- server rejection ---------- */

  const FIELD_ALIASES: Record<string, ConfiguratorField> = {
    widthCm: "widthCm", width: "widthCm",
    heightCm: "heightCm", height: "heightCm",
    quantity: "quantity",
    materialId: "materialId", material: "materialId",
    addonIds: "addonIds", addons: "addonIds",
    requestedDate: "requestedDate", date: "requestedDate",
  };

  function applyServerRejection(rejection: ServerFieldRejection): ConfiguratorEffect {
    const serverErrors = { ...state.validation.serverErrors };

    for (const fe of rejection.fieldErrors) {
      const field = FIELD_ALIASES[fe.field];
      if (field) serverErrors[field] = { code: fe.code };
    }

    let addonIds = state.addonIds;
    let materialId = state.materialId;

    if (rejection.code === "OPTION_UNAVAILABLE" && rejection.invalidOptionIds.length > 0) {
      const invalid = new Set(rejection.invalidOptionIds);
      // Addons are removed automatically; material is never silently swapped.
      if (materialId !== null && invalid.has(materialId)) {
        materialId = null;
        serverErrors.materialId = { code: "MATERIAL_UNAVAILABLE" };
      }
      addonIds = canonicalAddonIds(addonIds.filter(id => !invalid.has(id)));
    }

    state = { ...state, addonIds, materialId, validation: { ...state.validation, serverErrors } };
    emit();

    // Client errors are untouched; a configuration the server rejected is not re-quoted here.
    return { type: "NONE" };
  }

  /* ---------- schema ---------- */

  function replaceSchema(next: ConfiguratorSchema): ConfiguratorReconciliationResult {
    schema = next;

    const removedAddonIds = state.addonIds.filter(id => !addonIsSelectable(id));
    const addonIds = canonicalAddonIds(state.addonIds.filter(id => addonIsSelectable(id)));

    const materialInvalidated = state.materialId !== null && !materialIsSelectable(state.materialId);
    const materialId = materialInvalidated ? null : state.materialId;

    // Re-run dimension validation against the new bounds, but only for fields
    // the user has already committed — untouched fields stay silent.
    const clientErrors = { ...state.validation.clientErrors };
    for (const field of ["widthCm", "heightCm"] as DimensionField[]) {
      const key = field === "widthCm" ? "width" : "height";
      if (!state[key].touched) continue;
      const err = validateDimension(state[key].value, dimensionBounds(field));
      if (err) clientErrors[field] = err; else delete clientErrors[field];
    }
    const qErr =
      state.quantity.value === null
        ? (state.quantity.touched ? { code: "REQUIRED" } : null)
        : validateQuantity(state.quantity.value, schema.constraints);
    if (qErr) clientErrors.quantity = qErr; else delete clientErrors.quantity;
    if (materialInvalidated) clientErrors.materialId = { code: "MATERIAL_UNAVAILABLE" };

    state = { ...state, addonIds, materialId, validation: { ...state.validation, clientErrors } };
    emit();

    return { removedAddonIds, materialInvalidated, effect: effectFromCurrentConfiguration() };
  }

  /* ---------- draft ---------- */

  function getDraftSnapshot(): ConfiguratorDraftSnapshot {
    return {
      widthRaw: state.width.raw,
      heightRaw: state.height.raw,
      quantityRaw: state.quantity.raw,
      materialId: state.materialId,
      addonIds: [...state.addonIds],
      requestedDate: state.requestedDate,
    };
  }

  function restoreDraft(snapshot: ConfiguratorDraftSnapshot): ConfiguratorReconciliationResult {
    const widthValue = parseDimensionInput(snapshot.widthRaw);
    const heightValue = parseDimensionInput(snapshot.heightRaw);
    const quantityParsed = parseDimensionInput(snapshot.quantityRaw);

    const materialValid = snapshot.materialId !== null && materialIsSelectable(snapshot.materialId);
    const materialId = materialValid ? snapshot.materialId : null;
    const materialInvalidated = snapshot.materialId !== null && !materialValid;

    const requestedAddons = canonicalAddonIds(snapshot.addonIds);
    const addonIds = requestedAddons.filter(addonIsSelectable);
    const removedAddonIds = requestedAddons.filter(id => !addonIds.includes(id));

    state = {
      ...state,
      width: { raw: snapshot.widthRaw, value: widthValue, touched: false, editRevision: state.width.editRevision + 1 },
      height: { raw: snapshot.heightRaw, value: heightValue, touched: false, editRevision: state.height.editRevision + 1 },
      quantity: { raw: snapshot.quantityRaw, value: quantityParsed, touched: false, editRevision: state.quantity.editRevision + 1 },
      materialId,
      addonIds,
      requestedDate: snapshot.requestedDate,
      validation: { clientErrors: {}, serverErrors: {} },
      submitAttempted: false,
    };
    emit();

    return { removedAddonIds, materialInvalidated, effect: effectFromCurrentConfiguration() };
  }

  /* ---------- submit ---------- */

  function validateForSubmit(): ConfiguratorSubmitResult {
    const clientErrors: Partial<Record<ConfiguratorField, FieldError>> = {};

    const wErr = validateDimension(state.width.value, dimensionBounds("widthCm"));
    if (wErr) clientErrors.widthCm = wErr;

    const hErr = validateDimension(state.height.value, dimensionBounds("heightCm"));
    if (hErr) clientErrors.heightCm = hErr;

    const qErr =
      state.quantity.value === null
        ? { code: "REQUIRED" }
        : validateQuantity(state.quantity.value, schema.constraints);
    if (qErr) clientErrors.quantity = qErr;

    if (!materialIsSelectable(state.materialId)) {
      clientErrors.materialId = { code: state.materialId === null ? "REQUIRED" : "MATERIAL_UNAVAILABLE" };
    }
    if (!state.addonIds.every(addonIsSelectable)) {
      clientErrors.addonIds = { code: "ADDON_UNAVAILABLE" };
    }
    const dateError = requestedDateError(state.requestedDate);
    if (dateError) clientErrors.requestedDate = dateError;

    state = {
      ...state,
      submitAttempted: true,
      width: { ...state.width, touched: true },
      height: { ...state.height, touched: true },
      quantity: { ...state.quantity, touched: true },
      validation: { ...state.validation, clientErrors },
    };
    emit();

    const errorFields = FIELD_DOM_ORDER.filter(
      f => clientErrors[f] !== undefined || state.validation.serverErrors[f] !== undefined
    );

    if (errorFields.length > 0) return { valid: false, errorFields };

    const configuration = computeConfiguration();
    if (!configuration) return { valid: false, errorFields: ["widthCm"] };

    return { valid: true, configuration };
  }

  return {
    getState: () => state,
    getPricingConfiguration: computeConfiguration,
    setDimensionInput,
    commitDimension,
    getDimensionEditRevision,
    setQuantity,
    stepQuantity,
    setQuantityInput,
    commitQuantity,
    getQuantityEditRevision,
    setMaterial,
    toggleAddon,
    setRequestedDate,
    applyServerRejection,
    replaceSchema,
    getDraftSnapshot,
    restoreDraft,
    validateForSubmit,
    subscribe(listener) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    dispose() { disposed = true; listeners.clear(); },
  };
}
